import { useCountdown } from '@/hooks/useCountdown.hook';
import { CloseOutlined } from '@ant-design/icons';
import React, {
	ReactNode,
	startTransition,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useTranslation } from 'react-i18next';

import { cn, delay as onDelay, getTranslatedMessage, NOOP } from 'utils';

import { useOnClickOutside } from 'hooks';
import { TOptions } from 'i18next';
import { find } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

export interface IAction {
	text: string;
	onClick: () => void;
	style?: 'cancel' | 'next';
}

const defaultActions: IAction[] = [
	{
		text: '취소',
		onClick: NOOP,
		style: 'cancel',
	},
	{ text: '확인', onClick: NOOP, style: 'next' },
];

const AlertView = ({
	onClose,
	title,
	message = '',
	actions = defaultActions,
	closeWhenClickOutside = true,
	isShowCloseIcon,
	delay = 0,
	isShowFooter = true,
	hideDivide = false,
	messageOptions,
	countdownMs,
}: {
	onClose: () => void;
	title: string | ReactNode;
	message?: string | ReactNode;
	actions?: IAction[];
	closeWhenClickOutside?: boolean;
	isShowCloseIcon?: boolean;
	delay?: number;
	isShowFooter?: boolean;
	hideDivide?: boolean;
	messageOptions?: TOptions;
	countdownMs?: number;
}) => {
	const { t } = useTranslation('message');

	useEffect(() => {
		window.addEventListener('popstate', () => {
			onClose();
		});

		return () => {
			window.removeEventListener('popstate', () => null);
		};
	}, []);
	const ref = useRef(null);
	const cancelAction = find((item: IAction) => item.style === 'cancel')(
		actions
	);
	const nextAction = find((item: IAction) => item.style === 'next')(actions);

	const onNext = async () => {
		onClose();
		!!delay && (await onDelay(delay));
		nextAction?.onClick?.();
	};

	const onCancel = () => {
		cancelAction?.onClick?.();
		onClose();
	};

	// Text cua nut: neu caller truyen text (cancelText/confirmText) thi text do
	// phai duoc dung. Truoc day defaultValue la t('cancel')/t('confirm'), ma
	// getTranslatedMessage tra ve defaultValue khi key KHONG co trong file dich
	// -> moi text tu do (vd '저장') deu bi thay bang ban dich mac dinh.
	// Giu defaultValue = chinh text do, chi fallback khi caller khong truyen gi.
	const renderActionText = (
		text: string | undefined,
		fallback: ReactNode
	): ReactNode =>
		isNotNilOrEmpty(text)
			? (getTranslatedMessage(t, text, {
					ns: 'message',
					defaultValue: text,
				}) as ReactNode)
			: fallback;

	useOnClickOutside(ref, () => {
		if (closeWhenClickOutside) {
			onCancel();
		}
	});

	const countdown = useCountdown(countdownMs ?? 0);
	const countdownSeconds = useMemo(() => {
		return countdownMs != null
			? countdown.minutes * 60 + countdown.seconds
			: null;
	}, [countdown.minutes, countdown.seconds, countdownMs]);

	const displayMessageOptions = useMemo(() => {
		return countdownSeconds != null
			? { ...(messageOptions ?? {}), seconds: countdownSeconds }
			: messageOptions ?? {};
	}, [countdownSeconds, messageOptions]);

	useEffect(() => {
		if (countdownMs == null) return;
		countdown.handleStart();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- start once when countdown is enabled
	}, [countdownMs]);

	return (
		<div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 center overflow-hidden container-alert">
			<div
				className="xl:w-[320px] w-[324px] xl:h-[155px] h-[186px]  rounded-[8px] xl:rounded-none bg-white col overflow-hidden relative"
				ref={ref}
			>
				{isShowCloseIcon && (
					<button
						className="absolute z-20 p-4 bg-transparent top-1 right-1 center"
						onClick={onClose}
					>
						<CloseOutlined />
					</button>
				)}

				<div
					className={cn(
						'col flex-1 center xl:h-[120px] h-[136px] px-5',
						!hideDivide && 'divide-y divide-dashed divide-[#e0e3ec]'
					)}
				>
					<p
						className={cn(
							'text-base xl:text-[13px] font-normal text-center text-font2 whitespace-pre-wrap',
							message && 'font-medium pb-4'
						)}
					>
						{getTranslatedMessage(t, title, { ns: 'message' })}
					</p>

					<p className="text-sm xl:text-xs text-font3 text-center whitespace-pre-wrap !border-none">
						{getTranslatedMessage(t, message, {
							ns: 'message',
							options: {
								...displayMessageOptions,
							},
						})}
					</p>
				</div>
				{isShowFooter && (
					<div className="xl:h-[35px] h-[50px] border-t border-t-blue2 w-full row ">
						{isNotNilOrEmpty(cancelAction?.text) && (
							<button
								onClick={onCancel}
								className="flex flex-1 h-full bg-white center text-black xl:text-[14px] text-base"
							>
								{renderActionText(cancelAction?.text, t('cancel'))}
							</button>
						)}
						<button
							onClick={onNext}
							className="flex flex-1 h-full text-white center bg-blue2 xl:text-[14px] text-base"
						>
							{renderActionText(nextAction?.text, t('confirm'))}
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

// Create Stateless Modal handler from component with HOF
class AlertHandler extends React.Component {
	div: HTMLDivElement;
	root: Root | null = null;

	constructor(props: any) {
		super(props);
		this.div = document.createElement('div');
		this.root = createRoot(this.div);
	}

	callOnHide = () => {
		//
	};
	callOnShow = () => {
		//
	};
	hide = () => {
		this.callOnHide();
		if (this.root) {
			this.root.unmount();
			this.root = null;
		}
		if (this.div.parentNode) {
			this.div.parentNode.removeChild(this.div);
		}
	};
	alert = (
		title: string | ReactNode,
		message?: string | ReactNode,
		actions?: IAction[],
		options?: {
			closeWhenClickOutside?: boolean;
			isOneButton?: boolean;
			isShowCloseIcon?: boolean;
			delay?: number;
			isShowFooter?: boolean;
			hideDivide?: boolean;
			messageOptions?: Record<string, any>;
			countdownMs?: number;
		}
	) => {
		this.callOnShow();
		document.body.appendChild(this.div);

		if (!this.root) {
			this.root = createRoot(this.div);
		}

		startTransition(() => {
			this.root?.render(
				<AlertView
					onClose={this.hide}
					title={title}
					message={message}
					actions={actions}
					closeWhenClickOutside={options?.closeWhenClickOutside}
					isShowCloseIcon={options?.isShowCloseIcon}
					delay={options?.delay}
					isShowFooter={options?.isShowFooter}
					hideDivide={options?.hideDivide}
					messageOptions={options?.messageOptions}
					countdownMs={options?.countdownMs}
				/>
			);
		});
	};
}
export const Alert = new AlertHandler({});
